import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Tarjeta } from '@/components/ui/data';
import { Campo, Input, InputHora } from '@/components/ui/form';
import { avisarError } from '@/lib/avisos';

interface Sucursal {
  id: string;
  nombre: string;
  direccion: string | null;
  telefono: string | null;
  apertura: string;
  cierre: string;
  estaciones: number;
}

export function SucursalPage() {
  const { sucursalId, puede } = useSesion();
  const qc = useQueryClient();
  const sucursales = useQuery({ queryKey: ['sucursales'], queryFn: () => api<Sucursal[]>('/sucursales') });
  const sucursal = sucursales.data?.find((s) => s.id === sucursalId);
  const [datos, setDatos] = useState<Sucursal | null>(null);
  useEffect(() => setDatos(sucursal ?? null), [sucursal]);
  const editable = puede('config.editar');

  const guardar = useMutation({
    mutationFn: (d: Sucursal) =>
      api(`/sucursales/${d.id}`, {
        method: 'PATCH',
        body: {
          nombre: d.nombre,
          direccion: d.direccion || null,
          telefono: d.telefono || null,
          apertura: d.apertura,
          cierre: d.cierre,
          estaciones: d.estaciones,
        },
      }),
    onSuccess: () => {
      toast.success('Sucursal actualizada');
      void qc.invalidateQueries({ queryKey: ['sucursales'] });
    },
    onError: (e) => avisarError(e),
  });

  const set = <K extends keyof Sucursal>(campo: K, valor: Sucursal[K]) => setDatos((d) => (d ? { ...d, [campo]: valor } : d));

  return (
    <ModuleLayout titulo="Configuración" secciones={SECCIONES_POR_MODULO.configuracion}>
      <EncabezadoPagina titulo="Sucursal" descripcion="Datos de la sucursal, horario de atención y número de estaciones." />
      <Tarjeta className="max-w-2xl p-5">
        {!datos ? (
          <Esqueleto className="h-48" />
        ) : (
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              guardar.mutate(datos);
            }}
          >
            <fieldset disabled={!editable} className="contents">
              <Campo etiqueta="Nombre" requerido className="sm:col-span-2">
                {(p) => <Input {...p} value={datos.nombre} onChange={(e) => set('nombre', e.target.value)} />}
              </Campo>
              <Campo etiqueta="Dirección" className="sm:col-span-2">
                {(p) => <Input {...p} value={datos.direccion ?? ''} onChange={(e) => set('direccion', e.target.value)} />}
              </Campo>
              <Campo etiqueta="Teléfono">
                {(p) => <Input {...p} type="tel" value={datos.telefono ?? ''} onChange={(e) => set('telefono', e.target.value)} />}
              </Campo>
              <Campo etiqueta="Estaciones de trabajo">
                {(p) => (
                  <Input {...p} type="number" min={1} value={datos.estaciones} onChange={(e) => set('estaciones', Number(e.target.value))} className="cifras" />
                )}
              </Campo>
              <Campo etiqueta="Apertura">
                {(p) => <InputHora {...p} value={datos.apertura} onChange={(v) => set('apertura', v)} pasoMin={15} />}
              </Campo>
              <Campo etiqueta="Cierre">
                {(p) => <InputHora {...p} value={datos.cierre} onChange={(v) => set('cierre', v)} pasoMin={15} />}
              </Campo>
            </fieldset>
            {editable && (
              <div className="flex justify-end sm:col-span-2">
                <Button type="submit" cargando={guardar.isPending}>
                  Guardar cambios
                </Button>
              </div>
            )}
          </form>
        )}
      </Tarjeta>
    </ModuleLayout>
  );
}
